import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Wallet, Volume2, VolumeX } from "lucide-react";

/**
 * Animated Money Cycle Chart
 * Takes the user step-by-step explaining how their money flows through the cycle.
 * Uses values from My Numbers.
 * Wealth Loop (upper) vs Poverty Loop (lower)
 * Now with voice narration using Web Speech API
 */

const COLORS = {
  navy: "#1E3A8A",
  emerald: "#2ECC71",
  green: "#3E7A29",
  blue: "#1B74E4",
  yellow: "#F1C40F",
  red: "#DC2626",
  darkRed: "#8B0000",
  purple: "#6C43E0",
  gray: "#4A5568",
  dark: "#111827",
  light: "#F3F4F6",
};

const fmt = (n) => {
  const amount = Math.round(n || 0);
  return `$${amount.toLocaleString()}`;
};

// Format number for speech (e.g., $27,000 becomes "27 thousand dollars")
const fmtSpeech = (n) => {
  const amount = Math.round(n || 0);
  if (amount >= 1000000) {
    const millions = (amount / 1000000).toFixed(1);
    return `${millions} million dollars`;
  } else if (amount >= 1000) {
    const thousands = Math.round(amount / 1000);
    return `${thousands} thousand dollars`;
  }
  return `${amount} dollars`;
};

// Animation steps definition with dynamic speech text
const STEPS = [
  {
    id: 'intro',
    title: 'Your Money Cycle',
    description: 'Let\'s trace how your money flows through your financial life. This will show you whether you\'re in a Wealth Loop or a Poverty Loop.',
    getSpeech: () => "Let's trace how your money flows through your financial life. This will show you whether you're in a Wealth Loop or a Poverty Loop.",
    highlight: [],
    activeArrows: [],
  },
  {
    id: 'income',
    title: 'Income',
    description: 'Your journey starts here. This is all the money coming in from your work, salary, or wages.',
    getSpeech: (data) => `Your journey starts here with your income. You bring in ${fmtSpeech(data.income)} every month from your work.`,
    highlight: ['income'],
    activeArrows: [],
    getValue: (data) => data.income,
  },
  {
    id: 'expenses',
    title: 'Expenses',
    description: 'From your income, you pay for living expenses - housing, food, transportation, and other necessities.',
    getSpeech: (data) => `From your income, you pay ${fmtSpeech(data.expenses)} in monthly expenses for housing, food, transportation, and other necessities.`,
    highlight: ['income', 'expenses'],
    activeArrows: [],
    getValue: (data) => data.expenses,
  },
  {
    id: 'result',
    title: 'The Critical Moment',
    description: 'Income minus Expenses equals your monthly result. This determines which path you take.',
    getSpeech: (data) => {
      const surplus = data.income - data.expenses;
      if (surplus > 0) {
        return `Income minus expenses equals ${fmtSpeech(surplus)}. This positive result means you're heading toward the Wealth Loop!`;
      } else if (surplus < 0) {
        return `Income minus expenses equals negative ${fmtSpeech(Math.abs(surplus))}. This deficit means you're in the Poverty Loop.`;
      }
      return "Income minus expenses equals zero. You're breaking even, but not building wealth.";
    },
    highlight: ['income', 'expenses', 'result'],
    activeArrows: ['income-to-result'],
    getValue: (data) => data.income - data.expenses,
  },
  {
    id: 'gains-path',
    title: 'Wealth Loop: Gains',
    description: 'When income exceeds expenses, you have GAINS! This surplus is the seed of wealth building.',
    getSpeech: (data) => `Congratulations! With ${fmtSpeech(data.income - data.expenses)} in monthly gains, you have surplus money. This is the seed of wealth building!`,
    highlight: ['gains'],
    activeArrows: ['to-gains'],
    condition: (data) => data.income - data.expenses > 0,
  },
  {
    id: 'savings',
    title: 'Wealth Loop: Savings',
    description: 'Your gains go into savings first. This is your financial foundation.',
    getSpeech: (data) => `Your gains flow into savings. You're putting away ${fmtSpeech(data.savingsMonthly)} each month. This is your financial foundation.`,
    highlight: ['gains', 'savings'],
    activeArrows: ['gains-to-savings'],
    condition: (data) => data.income - data.expenses > 0,
    getValue: (data) => data.savingsMonthly,
  },
  {
    id: 'emergency',
    title: 'Wealth Loop: Emergency Fund',
    description: 'Part of your savings builds an emergency fund - your safety net for unexpected events.',
    getSpeech: (data) => `Part of your savings builds an emergency fund. You have ${fmtSpeech(data.emergencyFund)} set aside as your safety net for unexpected events.`,
    highlight: ['savings', 'emergency'],
    activeArrows: ['savings-to-emergency'],
    condition: (data) => data.income - data.expenses > 0,
    getValue: (data) => data.emergencyFund,
  },
  {
    id: 'investments',
    title: 'Wealth Loop: Investments',
    description: 'The magic happens here! Your money is put to work generating passive income.',
    getSpeech: (data) => `Now the magic happens! You have ${fmtSpeech(data.totalInvestments)} in investments. Your money is now working for you, generating passive income.`,
    highlight: ['savings', 'investments'],
    activeArrows: ['savings-to-investments'],
    condition: (data) => data.income - data.expenses > 0,
    getValue: (data) => data.totalInvestments,
  },
  {
    id: 'passive-income',
    title: 'Wealth Loop: Passive Income',
    description: 'Your investments generate MORE INCOME! This creates a positive feedback loop - the Wealth Cycle.',
    getSpeech: (data) => `Your investments generate ${fmtSpeech(data.passiveIncome)} in passive income every month! This creates a positive feedback loop - the Wealth Cycle.`,
    highlight: ['investments', 'moreIncome'],
    activeArrows: ['investments-to-income'],
    condition: (data) => data.income - data.expenses > 0,
    getValue: (data) => data.passiveIncome,
  },
  {
    id: 'wealth-complete',
    title: 'The Wealth Loop Complete!',
    description: 'More passive income + your salary = Even more savings = More investments = Even more passive income. This is how wealth compounds!',
    getSpeech: () => "The Wealth Loop is complete! More passive income plus your salary equals even more savings, which means more investments, which generates even more passive income. This is how wealth compounds over time!",
    highlight: ['income', 'gains', 'savings', 'investments', 'moreIncome', 'wealthPath'],
    activeArrows: ['full-wealth-cycle'],
    condition: (data) => data.income - data.expenses > 0,
  },
  {
    id: 'loss-path',
    title: 'Poverty Loop: Loss',
    description: 'When expenses exceed income, you have a LOSS. This leads to a very different path.',
    getSpeech: (data) => `When expenses exceed income, you have a loss of ${fmtSpeech(Math.abs(data.income - data.expenses))} every month. This leads to a very different path.`,
    highlight: ['loss'],
    activeArrows: ['to-loss'],
    condition: (data) => data.income - data.expenses <= 0,
  },
  {
    id: 'debt',
    title: 'Poverty Loop: Debt',
    description: 'Losses are covered by taking on DEBT - credit cards, loans, borrowing.',
    getSpeech: (data) => `Losses are covered by taking on debt. You currently have ${fmtSpeech(data.totalDebt)} in debt from credit cards, loans, and other borrowing.`,
    highlight: ['loss', 'debt'],
    activeArrows: ['loss-to-debt'],
    condition: (data) => data.income - data.expenses <= 0 || data.totalDebt > 0,
    getValue: (data) => data.totalDebt,
  },
  {
    id: 'payments',
    title: 'Poverty Loop: Debt Payments',
    description: 'Debt requires PAYMENTS with interest. This drains your future income.',
    getSpeech: (data) => `Debt requires payments with interest. You're paying ${fmtSpeech(data.debtPayments)} every month just to service your debt. This drains your future income.`,
    highlight: ['debt', 'payments'],
    activeArrows: ['debt-to-payments'],
    condition: (data) => data.income - data.expenses <= 0 || data.totalDebt > 0,
    getValue: (data) => data.debtPayments,
  },
  {
    id: 'more-expenses',
    title: 'Poverty Loop: More Expenses',
    description: 'Debt payments become MORE EXPENSES, making it even harder to have gains.',
    getSpeech: (data) => `Those debt payments of ${fmtSpeech(data.debtPayments)} become additional expenses every month, making it even harder to have gains.`,
    highlight: ['payments', 'moreExpenses'],
    activeArrows: ['payments-to-expenses'],
    condition: (data) => data.income - data.expenses <= 0 || data.totalDebt > 0,
  },
  {
    id: 'poverty-complete',
    title: 'The Poverty Loop',
    description: 'More debt = More payments = More expenses = Bigger loss = More debt. This is the trap that keeps people struggling.',
    getSpeech: () => "The Poverty Loop is complete. More debt means more payments, which means more expenses, which creates a bigger loss, which leads to more debt. This is the trap that keeps people struggling financially.",
    highlight: ['loss', 'debt', 'payments', 'moreExpenses', 'povertyPath'],
    activeArrows: ['full-poverty-cycle'],
    condition: (data) => data.income - data.expenses <= 0 || data.totalDebt > 0,
  },
  {
    id: 'summary',
    title: 'Your Position',
    description: 'Based on your numbers, you are currently in the {position}. {advice}',
    getSpeech: (data) => {
      const isWealth = data.income - data.expenses > 0;
      if (isWealth) {
        return `Based on your numbers, you are currently in the Wealth Loop with a monthly surplus of ${fmtSpeech(data.income - data.expenses)}. Keep building! Increase your savings rate and let compound growth work for you.`;
      }
      return `Based on your numbers, you are currently in the Poverty Loop with a monthly deficit of ${fmtSpeech(Math.abs(data.income - data.expenses))}. Focus on reducing expenses or increasing income to break free from the debt cycle.`;
    },
    highlight: ['all'],
    activeArrows: [],
  },
];

const AnimatedMoneyCycleChart = ({ 
  income = 0,
  expenses = 0,
  savingsMonthly = 0,
  passiveIncome = 0,
  emergencyFund = 0,
  totalInvestments = 0,
  totalDebt = 0,
  debtPayments = 0,
  onNavigateToMyNumbers,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [animatedValue, setAnimatedValue] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const speechSynthRef = useRef(null);

  const data = {
    income,
    expenses,
    savingsMonthly,
    passiveIncome,
    emergencyFund,
    totalInvestments,
    totalDebt,
    debtPayments,
  };

  const surplus = income - expenses;
  const isWealthLoop = surplus > 0;

  // Initialize speech synthesis and get available voices
  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      speechSynthRef.current = window.speechSynthesis;
      
      const loadVoices = () => {
        const voices = speechSynthRef.current.getVoices();
        console.log('Loaded voices:', voices.length);
        
        // Prefer English voices
        const englishVoices = voices.filter(v => v.lang.startsWith('en'));
        setAvailableVoices(englishVoices.length > 0 ? englishVoices : voices);
        
        // Select a good default voice (prefer female voices for friendliness)
        const preferredVoice = englishVoices.find(v => 
          v.name.includes('Samantha') || 
          v.name.includes('Google') || 
          v.name.includes('Microsoft') ||
          v.name.includes('Female')
        ) || englishVoices[0] || voices[0];
        
        if (preferredVoice) {
          setSelectedVoice(preferredVoice);
          console.log('Selected voice:', preferredVoice.name);
        }
      };
      
      // Load voices (may be async in some browsers)
      loadVoices();
      
      // Chrome loads voices asynchronously
      if (speechSynthRef.current.onvoiceschanged !== undefined) {
        speechSynthRef.current.onvoiceschanged = loadVoices;
      }
      
      // Fallback: try loading again after a short delay (for some browsers)
      setTimeout(loadVoices, 100);
    } else {
      console.warn('Speech synthesis not supported in this browser');
    }
    
    return () => {
      // Cancel any ongoing speech when component unmounts
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
    };
  }, []);

  // Speak function
  const speak = useCallback((text) => {
    if (!speechSynthRef.current || !voiceEnabled) {
      console.log('Speech skipped:', !speechSynthRef.current ? 'no synth' : 'voice disabled');
      return;
    }
    
    // Cancel any ongoing speech
    speechSynthRef.current.cancel();
    
    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95; // Slightly slower for clarity
      utterance.pitch = 1;
      utterance.volume = 1;
      
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      
      utterance.onstart = () => {
        console.log('Speech started');
        setIsSpeaking(true);
      };
      utterance.onend = () => {
        console.log('Speech ended');
        setIsSpeaking(false);
      };
      utterance.onerror = (event) => {
        console.error('Speech error:', event.error);
        setIsSpeaking(false);
      };
      
      speechSynthRef.current.speak(utterance);
    } catch (error) {
      console.error('Speech synthesis error:', error);
      setIsSpeaking(false);
    }
  }, [voiceEnabled, selectedVoice]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (speechSynthRef.current) {
      speechSynthRef.current.cancel();
      setIsSpeaking(false);
    }
  }, []);

  // Filter steps based on user's situation
  const getRelevantSteps = useCallback(() => {
    return STEPS.filter(step => {
      if (!step.condition) return true;
      return step.condition(data);
    });
  }, [data]);

  const relevantSteps = getRelevantSteps();
  const step = relevantSteps[currentStep] || relevantSteps[0];

  // Speak when step changes and voice is enabled
  useEffect(() => {
    if (voiceEnabled && step.getSpeech) {
      const speechText = step.getSpeech(data);
      speak(speechText);
    }
  }, [currentStep, voiceEnabled, step, data, speak]);

  // Auto-play functionality - waits for speech to finish if voice enabled
  useEffect(() => {
    let timer;
    if (isPlaying && currentStep < relevantSteps.length - 1) {
      // If voice is enabled, wait longer to allow speech to complete
      const delay = voiceEnabled ? 6000 : 4000;
      timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, delay);
    } else if (currentStep >= relevantSteps.length - 1) {
      setIsPlaying(false);
    }
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, relevantSteps.length, voiceEnabled]);

  // Animate value counter
  useEffect(() => {
    if (step.getValue) {
      const targetValue = step.getValue(data);
      setAnimatedValue(0);
      const duration = 1000;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setAnimatedValue(Math.round(targetValue * eased));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    } else {
      setAnimatedValue(null);
    }
  }, [currentStep, step, data]);

  const nextStep = () => {
    if (currentStep < relevantSteps.length - 1) {
      stopSpeaking();
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      stopSpeaking();
      setCurrentStep(prev => prev - 1);
    }
  };

  const reset = () => {
    stopSpeaking();
    setCurrentStep(0);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (currentStep >= relevantSteps.length - 1) {
      setCurrentStep(0);
    }
    setIsPlaying(!isPlaying);
  };

  const toggleVoice = () => {
    if (voiceEnabled) {
      stopSpeaking();
      setVoiceEnabled(false);
    } else {
      setVoiceEnabled(true);
      // Trigger speech for current step when enabling voice
      if (step.getSpeech) {
        // Small delay to ensure state is updated
        setTimeout(() => {
          if (speechSynthRef.current) {
            const utterance = new SpeechSynthesisUtterance(step.getSpeech(data));
            utterance.rate = 0.95;
            utterance.pitch = 1;
            utterance.volume = 1;
            if (selectedVoice) {
              utterance.voice = selectedVoice;
            }
            utterance.onstart = () => setIsSpeaking(true);
            utterance.onend = () => setIsSpeaking(false);
            utterance.onerror = (e) => {
              console.error('Speech error:', e);
              setIsSpeaking(false);
            };
            speechSynthRef.current.speak(utterance);
          }
        }, 100);
      }
    }
  };

  // Check if element should be highlighted
  const isHighlighted = (id) => step.highlight.includes(id) || step.highlight.includes('all');
  
  // Get description with dynamic values
  const getDescription = () => {
    let desc = step.description;
    if (step.id === 'summary') {
      const position = isWealthLoop ? 'WEALTH LOOP' : 'POVERTY LOOP';
      const advice = isWealthLoop 
        ? 'Keep building! Increase your savings rate and let compound growth work for you.'
        : 'Focus on reducing expenses or increasing income to break free from the debt cycle.';
      desc = desc.replace('{position}', position).replace('{advice}', advice);
    }
    return desc;
  };

  // Check if no data
  const hasNoData = income === 0 && expenses === 0;

  if (hasNoData) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl font-bold text-white">
            <Wallet className="w-6 h-6 text-blue-500" />
            Animated Money Cycle
          </CardTitle>
          <CardDescription className="text-gray-400">
            See how your money flows through the wealth or poverty cycle
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center py-8">
          <p className="text-gray-400 mb-4">Enter your financial data to see your personalized money flow</p>
          {onNavigateToMyNumbers && (
            <Button onClick={onNavigateToMyNumbers} className="bg-blue-600 hover:bg-blue-700">
              <Wallet className="w-4 h-4 mr-2" />
              Enter My Numbers
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl font-bold text-white">
          <Wallet className="w-6 h-6 text-blue-500" />
          Your Money Cycle Journey
        </CardTitle>
        <CardDescription className="text-gray-400">
          Step-by-step visualization of how your money flows
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all duration-500"
            style={{ 
              width: `${((currentStep + 1) / relevantSteps.length) * 100}%`,
              backgroundColor: isWealthLoop ? COLORS.green : COLORS.darkRed
            }}
          />
        </div>

        {/* Step indicator */}
        <div className="text-center text-sm text-gray-400">
          Step {currentStep + 1} of {relevantSteps.length}
        </div>

        {/* SVG Chart */}
        <div className="relative bg-gray-800/50 rounded-xl p-4" style={{ minHeight: '400px' }}>
          <svg viewBox="0 0 800 450" className="w-full h-full">
            {/* Arrow markers */}
            <defs>
              <marker id="arrow-wealth" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
                <path d="M0,0 L0,6 L9,3 z" fill={COLORS.green} />
              </marker>
              <marker id="arrow-poverty" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
                <path d="M0,0 L0,6 L9,3 z" fill={COLORS.darkRed} />
              </marker>
              <marker id="arrow-blue" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
                <path d="M0,0 L0,6 L9,3 z" fill={COLORS.blue} />
              </marker>
              {/* Glow filter */}
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>

            {/* Background labels */}
            <text x="400" y="30" fontSize="14" fill={COLORS.navy} textAnchor="middle" fontWeight="bold" opacity={isHighlighted('wealthPath') ? 1 : 0.3}>
              WEALTH LOOP
            </text>
            <text x="400" y="440" fontSize="14" fill={COLORS.darkRed} textAnchor="middle" fontWeight="bold" opacity={isHighlighted('povertyPath') ? 1 : 0.3}>
              POVERTY LOOP
            </text>

            {/* Dividing line */}
            <line x1="0" y1="225" x2="800" y2="225" stroke="#374151" strokeWidth="2" strokeDasharray="10,5" />

            {/* ===== INCOME BOX ===== */}
            <g style={{ 
              opacity: isHighlighted('income') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('income') ? 'url(#glow)' : 'none'
            }}>
              <rect x="20" y="185" width="120" height="80" rx="8" fill={COLORS.blue} />
              <text x="80" y="215" fontSize="16" fontWeight="bold" fill="white" textAnchor="middle">INCOME</text>
              <text x="80" y="240" fontSize="14" fill="white" textAnchor="middle">{fmt(income)}</text>
            </g>

            {/* ===== EXPENSES BOX ===== */}
            <g style={{ 
              opacity: isHighlighted('expenses') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('expenses') ? 'url(#glow)' : 'none'
            }}>
              <rect x="180" y="185" width="120" height="80" rx="8" fill={COLORS.red} />
              <text x="240" y="215" fontSize="16" fontWeight="bold" fill="white" textAnchor="middle">EXPENSES</text>
              <text x="240" y="240" fontSize="14" fill="white" textAnchor="middle">{fmt(expenses)}</text>
            </g>

            {/* Minus sign */}
            <text x="155" y="235" fontSize="30" fill="#9CA3AF" textAnchor="middle">−</text>

            {/* Equals sign */}
            <text x="315" y="235" fontSize="30" fill="#9CA3AF" textAnchor="middle">=</text>

            {/* ===== RESULT/GAINS BOX (Upper) ===== */}
            <g style={{ 
              opacity: isHighlighted('gains') || isHighlighted('result') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('gains') ? 'url(#glow)' : 'none'
            }}>
              <rect x="340" y="100" width="120" height="80" rx="8" fill={surplus > 0 ? COLORS.green : '#374151'} stroke={COLORS.green} strokeWidth="2" />
              <text x="400" y="130" fontSize="16" fontWeight="bold" fill="white" textAnchor="middle">GAINS</text>
              <text x="400" y="155" fontSize="14" fill="white" textAnchor="middle">{fmt(Math.max(0, surplus))}</text>
            </g>

            {/* ===== ZERO BOX (Middle) ===== */}
            <g style={{ 
              opacity: isHighlighted('result') && surplus === 0 ? 1 : 0.2,
              transition: 'all 0.5s ease'
            }}>
              <rect x="340" y="195" width="120" height="60" rx="8" fill={COLORS.gray} />
              <text x="400" y="230" fontSize="16" fontWeight="bold" fill="white" textAnchor="middle">ZERO</text>
            </g>

            {/* ===== LOSS BOX (Lower) ===== */}
            <g style={{ 
              opacity: isHighlighted('loss') || isHighlighted('result') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('loss') ? 'url(#glow)' : 'none'
            }}>
              <rect x="340" y="270" width="120" height="80" rx="8" fill={surplus < 0 ? COLORS.darkRed : '#374151'} stroke={COLORS.darkRed} strokeWidth="2" />
              <text x="400" y="300" fontSize="16" fontWeight="bold" fill="white" textAnchor="middle">LOSS</text>
              <text x="400" y="325" fontSize="14" fill="white" textAnchor="middle">{fmt(Math.abs(Math.min(0, surplus)))}</text>
            </g>

            {/* Arrow from income/expenses to result */}
            {isHighlighted('result') && (
              <line x1="300" y1="225" x2="340" y2={surplus > 0 ? 140 : surplus < 0 ? 310 : 225} 
                stroke={surplus > 0 ? COLORS.green : surplus < 0 ? COLORS.darkRed : COLORS.gray} 
                strokeWidth="3" 
                markerEnd={surplus > 0 ? "url(#arrow-wealth)" : "url(#arrow-poverty)"}
                style={{ transition: 'all 0.5s ease' }}
              />
            )}

            {/* ===== WEALTH LOOP ELEMENTS ===== */}
            
            {/* Savings */}
            <g style={{ 
              opacity: isHighlighted('savings') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('savings') ? 'url(#glow)' : 'none'
            }}>
              <rect x="500" y="100" width="100" height="60" rx="8" fill={COLORS.green} />
              <text x="550" y="125" fontSize="14" fontWeight="bold" fill="white" textAnchor="middle">SAVINGS</text>
              <text x="550" y="145" fontSize="12" fill="white" textAnchor="middle">{fmt(savingsMonthly)}</text>
            </g>
            {isHighlighted('savings') && (
              <line x1="460" y1="140" x2="500" y2="130" stroke={COLORS.green} strokeWidth="2" markerEnd="url(#arrow-wealth)" />
            )}

            {/* Emergency Fund */}
            <g style={{ 
              opacity: isHighlighted('emergency') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('emergency') ? 'url(#glow)' : 'none'
            }}>
              <rect x="640" y="60" width="130" height="60" rx="8" fill={COLORS.green} />
              <text x="705" y="85" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle">EMERGENCY FUND</text>
              <text x="705" y="105" fontSize="12" fill="white" textAnchor="middle">{fmt(emergencyFund)}</text>
            </g>
            {isHighlighted('emergency') && (
              <line x1="600" y1="120" x2="640" y2="90" stroke={COLORS.green} strokeWidth="2" markerEnd="url(#arrow-wealth)" />
            )}

            {/* Investments */}
            <g style={{ 
              opacity: isHighlighted('investments') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('investments') ? 'url(#glow)' : 'none'
            }}>
              <rect x="640" y="140" width="130" height="60" rx="8" fill={COLORS.navy} />
              <text x="705" y="165" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle">INVESTMENTS</text>
              <text x="705" y="185" fontSize="12" fill="white" textAnchor="middle">{fmt(totalInvestments)}</text>
            </g>
            {isHighlighted('investments') && (
              <line x1="600" y1="130" x2="640" y2="160" stroke={COLORS.navy} strokeWidth="2" markerEnd="url(#arrow-blue)" />
            )}

            {/* More Income (Passive) */}
            <g style={{ 
              opacity: isHighlighted('moreIncome') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('moreIncome') ? 'url(#glow)' : 'none'
            }}>
              <rect x="180" y="60" width="120" height="60" rx="8" fill={COLORS.blue} />
              <text x="240" y="85" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle">PASSIVE INCOME</text>
              <text x="240" y="105" fontSize="12" fill="white" textAnchor="middle">{fmt(passiveIncome)}</text>
            </g>
            {isHighlighted('moreIncome') && (
              <>
                <line x1="640" y1="170" x2="300" y2="90" stroke={COLORS.navy} strokeWidth="2" markerEnd="url(#arrow-blue)" />
                <line x1="180" y1="90" x2="80" y2="185" stroke={COLORS.blue} strokeWidth="2" markerEnd="url(#arrow-blue)" />
              </>
            )}

            {/* Full wealth cycle arrows */}
            {isHighlighted('wealthPath') && (
              <path 
                d="M 80 265 Q 80 350 200 350 Q 400 350 400 180 Q 400 100 550 100 Q 700 100 700 170 Q 700 50 240 50 Q 80 50 80 185"
                fill="none"
                stroke={COLORS.green}
                strokeWidth="3"
                strokeDasharray="10,5"
                opacity="0.5"
              />
            )}

            {/* ===== POVERTY LOOP ELEMENTS ===== */}
            
            {/* Debt */}
            <g style={{ 
              opacity: isHighlighted('debt') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('debt') ? 'url(#glow)' : 'none'
            }}>
              <rect x="500" y="290" width="100" height="60" rx="8" fill={COLORS.darkRed} />
              <text x="550" y="315" fontSize="14" fontWeight="bold" fill="white" textAnchor="middle">DEBT</text>
              <text x="550" y="335" fontSize="12" fill="white" textAnchor="middle">{fmt(totalDebt)}</text>
            </g>
            {isHighlighted('debt') && (
              <line x1="460" y1="310" x2="500" y2="320" stroke={COLORS.darkRed} strokeWidth="2" markerEnd="url(#arrow-poverty)" />
            )}

            {/* Payments */}
            <g style={{ 
              opacity: isHighlighted('payments') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('payments') ? 'url(#glow)' : 'none'
            }}>
              <rect x="640" y="290" width="130" height="60" rx="8" fill={COLORS.red} />
              <text x="705" y="315" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle">PAYMENTS</text>
              <text x="705" y="335" fontSize="12" fill="white" textAnchor="middle">{fmt(debtPayments)}/mo</text>
            </g>
            {isHighlighted('payments') && (
              <line x1="600" y1="320" x2="640" y2="320" stroke={COLORS.red} strokeWidth="2" markerEnd="url(#arrow-poverty)" />
            )}

            {/* More Expenses */}
            <g style={{ 
              opacity: isHighlighted('moreExpenses') ? 1 : 0.3,
              transition: 'all 0.5s ease',
              filter: isHighlighted('moreExpenses') ? 'url(#glow)' : 'none'
            }}>
              <rect x="180" y="330" width="120" height="60" rx="8" fill={COLORS.red} />
              <text x="240" y="355" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle">MORE EXPENSES</text>
              <text x="240" y="375" fontSize="12" fill="white" textAnchor="middle">+{fmt(debtPayments)}</text>
            </g>
            {isHighlighted('moreExpenses') && (
              <>
                <line x1="705" y1="350" x2="705" y2="400" stroke={COLORS.red} strokeWidth="2" />
                <line x1="705" y1="400" x2="300" y2="360" stroke={COLORS.red} strokeWidth="2" markerEnd="url(#arrow-poverty)" />
                <line x1="180" y1="360" x2="240" y2="265" stroke={COLORS.red} strokeWidth="2" markerEnd="url(#arrow-poverty)" />
              </>
            )}

            {/* Full poverty cycle arrows */}
            {isHighlighted('povertyPath') && (
              <path 
                d="M 240 265 Q 240 350 400 350 Q 500 350 550 320 Q 650 320 705 320 Q 750 350 705 400 Q 400 420 240 390 Q 180 380 240 265"
                fill="none"
                stroke={COLORS.darkRed}
                strokeWidth="3"
                strokeDasharray="10,5"
                opacity="0.5"
              />
            )}
          </svg>
        </div>

        {/* Step Info Panel */}
        <div className={`rounded-xl p-6 ${isWealthLoop ? 'bg-green-900/30 border border-green-700/50' : 'bg-red-900/30 border border-red-700/50'}`}>
          <h3 className={`text-xl font-bold mb-2 ${isWealthLoop ? 'text-green-400' : 'text-red-400'}`}>
            {step.title}
          </h3>
          <p className="text-gray-300 text-sm leading-relaxed">
            {getDescription()}
          </p>
          {animatedValue !== null && (
            <div className={`mt-3 text-2xl font-bold ${isWealthLoop ? 'text-green-400' : 'text-red-400'}`}>
              {fmt(animatedValue)}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          
          <Button
            size="sm"
            onClick={togglePlay}
            className={isWealthLoop ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={nextStep}
            disabled={currentStep >= relevantSteps.length - 1}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          
          {/* Voice Toggle Button */}
          <div className="border-l border-gray-600 pl-3 ml-1">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleVoice}
              className={`transition-all ${
                voiceEnabled 
                  ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-700' 
                  : 'border-gray-600 text-gray-300 hover:bg-gray-700'
              }`}
              title={voiceEnabled ? 'Turn off voice narration' : 'Turn on voice narration'}
            >
              {voiceEnabled ? (
                <Volume2 className={`w-4 h-4 ${isSpeaking ? 'animate-pulse' : ''}`} />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
        
        {/* Voice indicator */}
        {voiceEnabled && (
          <div className="text-center text-xs text-blue-400 mt-2">
            {isSpeaking ? (
              <span className="flex items-center justify-center gap-1">
                <span className="animate-pulse">🔊</span> Speaking...
              </span>
            ) : availableVoices.length > 0 ? (
              <span>🔇 Voice narration enabled • Click Next or Play to hear</span>
            ) : (
              <span className="text-yellow-400">⚠️ No voices available - check browser settings</span>
            )}
          </div>
        )}

        {/* Your Status Summary */}
        {step.id === 'summary' && (
          <div className={`text-center p-4 rounded-lg ${isWealthLoop ? 'bg-green-900/50' : 'bg-red-900/50'}`}>
            <div className={`text-lg font-bold ${isWealthLoop ? 'text-green-400' : 'text-red-400'}`}>
              You are in the {isWealthLoop ? 'WEALTH LOOP' : 'POVERTY LOOP'}
            </div>
            <div className="text-gray-400 text-sm mt-1">
              Monthly {isWealthLoop ? 'Surplus' : 'Deficit'}: {fmt(Math.abs(surplus))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnimatedMoneyCycleChart;
